import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useRealtimeUnits } from 'Core/hooks';
import _ from 'lodash';

//Components
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { NotificationManager } from 'Components/Notifications';
import { Button } from "@gpstrackit/gtc-react-controls";

//Views
import Main from 'Modules/.common/components/CameraSetup/.components/cameraConfig/main';
import BukConfig from 'Modules/.common/components/CameraSetup/.components/cameraConfig/buk';

//Actions
import {
    cameraSetupGetCamerasDefaultConfigure,
    cameraSetupGetFleetDefaultConfigure,
    cameraSetupGetCameraConfiguration,
    cameraSetupUpdateVidFleetAddConfig,
    cameraSetupUpdateUnits,
    reset
} from './redux/slice';

//Helpers
import { getVidFleetConfigValues } from 'Core/data/Helpers';

//Styles.scss
import './styles.scss'

const CameraSetup = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const vidFleetConfigLimitDefault = getVidFleetConfigValues();

    //Variables
    let vehicleClassOptions = [
        {
            id: 1,
            name: messages['cameraSetup_heavy_duty'],
            eventThreshold: {
                accValue: 250,
                brakeValue: -335,
                turnValue: 295,
                accDuration: 16,
                brakeDuration: 16,
                turnDuration: 16,
                steps: [200, 200, 200, 200, 200, 200]
            },
            advanced: {
                adas: {
                    timeRatio: {
                        FORWARD_COLLISION: 80, // form 20 to 200 
                        HEADWAY_MONITORING: 60, // from 20 to 200
                    },
                    cameraHeight: 2.5,
                    vehicleWidth: 2.5
                }
            }
        },
        {
            id: 2,
            name: messages['cameraSetup_light_truck'],
            eventThreshold: {
                accValue: 280,
                brakeValue: -360,
                turnValue: 340,
                accDuration: 16,
                brakeDuration: 16,
                turnDuration: 16,
                steps: [200, 200, 200, 200, 200, 200]
            },
            advanced: {
                adas: {
                    timeRatio: {
                        FORWARD_COLLISION: 76, // form 20 to 200 
                        HEADWAY_MONITORING: 55, // from 20 to 200
                    },
                    cameraHeight: 2,
                    vehicleWidth: 2
                }
            }
        },
        {
            id: 3,
            name: messages['cameraSetup_sedan'],
            eventThreshold: {
                accValue: 300,
                brakeValue: -375,
                turnValue: 365,
                accDuration: 16,
                brakeDuration: 16,
                turnDuration: 16,
                steps: [200, 200, 200, 200, 200, 200]
            },
            advanced: {
                adas: {
                    timeRatio: {
                        FORWARD_COLLISION: 72, // form 20 to 200 
                        HEADWAY_MONITORING: 50, // from 20 to 200
                    },
                    cameraHeight: 1.8,
                    vehicleWidth: 1.8
                }
            }
        }
    ]

    //Redux States
    const unitsList = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });
    const {
        loadingCamerasDefaultConfigure,
        loadingFleetDefaultConfigure,
        loadingCameraConfiguration,
        camerasDefaultConfigure,
        fleetDefaultConfigure,
        cameraConfiguration,
        loadingDeviceModels,
        loadingUpdate,
        errorUpdate,
        result,
        units,
        open,
        bulk
    } = useSelector((state) => state.cameraSetupRedux);

    //Local States
    const [cameraConfigPillSelected, setCameraConfigPillSelected] = useState(0);
    const [confirmChanges, setConfirmChanges] = useState(false);
    const [formDataCamera, setFormDataCamera] = useState({});
    const [cameraChanges, setCameraChanges] = useState({});
    const [vehicleClass, setVehicleClass] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [errors, setErrors] = useState([]);
    const [deviceModelSelected, setDeviceModelSelected] = useState(null);
    const [selectedCameras, setSelectedCameras] = useState([]);
    const [tags, searchTags] = useState([]);

    //Function for calculating alarm counts
    const calculateAlarmCounts = (dataCamera, alarmTypes, defaultCount) => {
        return alarmTypes.reduce((acc, type) => {
            if(!type) {
                return acc;
            }
            acc[type] = {
                ...dataCamera.advanced?.safetyAssistance?.alarm?.[type],
                cnt: cameraConfiguration?.advanced?.safetyAssistance?.alarm?.[type]?.cnt || defaultCount,
                duration: cameraConfiguration?.advanced?.safetyAssistance?.alarm?.[type]?.duration || defaultCount * 10
            };
            return acc;
        }, {});
    };

    //Function for updating the structure of the configuration object
    const updateDataCamera = (dataCamera, alarmCounts) => {
        return {
            ...dataCamera,
            advanced: {
                ...dataCamera.advanced,
                safetyAssistance: {
                    ...dataCamera.advanced?.safetyAssistance,
                    alarm: {
                        ...dataCamera.advanced?.safetyAssistance?.alarm,
                        ...alarmCounts
                    }
                }
            }
        };
    };

    const removeUndefinedAndNull = (obj) => {
        return _.transform(obj, (result, value, key) => {
            if (_.isPlainObject(value)) {
                result[key] = removeUndefinedAndNull(value);
            } else if (!_.isUndefined(value) && !_.isNull(value)) {
                result[key] = value;
            }
            return result;
        }, {});
    };

    const filterByTagsOptimized = (data, tagIds, deviceModel) => {
        const tagIdSet = new Set(tagIds);
        return data.filter(item => item?.linkedUnit?.unitVersionProduct === deviceModel && (item.tags.some(tag => tagIdSet.has(tag.id)) || (item.tags.length === 0 && tagIdSet.has(-1))));
    }

    const applyChanges = () => {
        const data = removeUndefinedAndNull(cameraChanges)
        let unitIds = !tags.length ? units.map((item) => (item?.unitId)) : []
        let settings = JSON.stringify(data)

        if (tags.length) {
            let tagIds = tags.map((item) => item?.id)
            let currentUnits = filterByTagsOptimized(unitsList, tagIds, deviceModelSelected?.id)
            unitIds = currentUnits.map((item) => item?.linkedUnit?.id)
            if (!unitIds.length) {
                NotificationManager.warning(
                    messages['cameraSetup_no_camera_available'],
                    messages['cameraSetup_camera_setup'],
                    3000,
                )
            }
        }
        if (bulk && units.length && units[0].id === "all") {
            unitIds = [-1]
        }

        let payload = {
            unitIds,
            settings,
            deviceModelName: unitIds.includes(-1) ? deviceModelSelected?.id : null
        }

        dispatch(cameraSetupUpdateVidFleetAddConfig(payload))
    }

    const handleClose = () => {
        setCameraConfigPillSelected(0);
        setConfirmChanges(false);
        setFormDataCamera({});
        setVehicleClass(null);
        setCameraChanges({});
        dispatch(reset());
        setErrors([]);
    }

    const mergeObjects = (objeto1, objeto2) => {
        const nuevoObjeto = JSON.parse(JSON.stringify(objeto1));
        function mergeRecursive(obj1, obj2) {
            for (const propiedad in obj2) {
                if (!obj1.hasOwnProperty(propiedad)) {
                    obj1[propiedad] = obj2[propiedad];
                } else if (typeof obj1[propiedad] === 'object' && typeof obj2[propiedad] === 'object') {
                    mergeRecursive(obj1[propiedad], obj2[propiedad]);
                }
            }
        }
        mergeRecursive(nuevoObjeto, objeto2);
        return nuevoObjeto;
    }

    useEffect(() => {
        if ((cameraConfiguration || camerasDefaultConfigure) && fleetDefaultConfigure?.settings) {
            const alarmTypes = ['USING_CELLPHONE', 'NO_SEATBELT', 'DISTRACTED', 'NO_DRIVER', 'TAILGATING', 'LANE_DEPARTURE', 'PASSENGERS_NO_SEATBELT'];
            let defaultCount = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default || 0;

            let dataCamera;
            if (units?.length === 1 && cameraConfiguration && fleetDefaultConfigure.settings && !bulk) {
                if (cameraConfiguration?.noData) {
                    dataCamera = cameraConfiguration
                } else {
                    //Combines the camera configuration with the fleet configuration for a single unit
                    dataCamera = mergeObjects(cameraConfiguration, fleetDefaultConfigure.settings);
                }
            } else if (units?.length && camerasDefaultConfigure && fleetDefaultConfigure.settings && bulk) {
                //Combines default camera configuration with fleet configuration for multiple units
                dataCamera = mergeObjects(fleetDefaultConfigure.settings, camerasDefaultConfigure);
            }
            if (dataCamera) {
                const alarmCounts = calculateAlarmCounts(dataCamera, alarmTypes, defaultCount);
                const updatedDataCamera = updateDataCamera(dataCamera, alarmCounts);

                setFormDataCamera(updatedDataCamera);
            }
        }
    }, [cameraConfiguration, camerasDefaultConfigure, fleetDefaultConfigure, units])

    useEffect(() => {
        if (open) {
            if (units?.length === 1 && units[0].esn) {
                dispatch(cameraSetupGetCameraConfiguration({ esn: units[0].esn }))
            }
            dispatch(cameraSetupGetCamerasDefaultConfigure())
            dispatch(cameraSetupGetFleetDefaultConfigure())
        }
    }, [open])

    useEffect(() => {
        if (result) {
            if (result?.update) {
                NotificationManager.success(
                    messages['cameraSetup_success_messages'],
                    messages['cameraSetup_camera_setup'],
                    3000,
                )
                handleClose()
            } else {
                NotificationManager.error(
                    messages['cameraSetup_error_messages'],
                    messages['cameraSetup_camera_setup'],
                    3000,
                )
                dispatch(reset({ result: null }))
            }
        }
        if (errorUpdate) {
            dispatch(reset({ errorUpdate: null }))
        }
    }, [result, errorUpdate])
    return (
        <Modal
            title={bulk ? `${messages['cameraSetup_bulk_settings']} (${units?.length ? units[0].id === "all" ? "All" : units?.length : 0})` : `${messages['cameraSetup_camera_setup']} (${units?.length && units[0]?.unitLabel})`}
            open={open}
            loading={loadingCamerasDefaultConfigure || loadingFleetDefaultConfigure || loadingCameraConfiguration || loadingUpdate || loadingDeviceModels}
            position="right"
            size="small"
            handleClose={() => {
                if (confirmChanges) setConfirmChanges(false);
                else handleClose();
            }}
        >
            <ModalBody className="camera-setup-container">
                {
                    bulk &&
                    <BukConfig
                        setDeviceModelSelected={setDeviceModelSelected}
                        deviceModelSelected={deviceModelSelected}
                        currentStep={currentStep}
                        selectedCameras={selectedCameras}
                        setSelectedCameras={setSelectedCameras}
                        setCameraConfigPillSelected={setCameraConfigPillSelected}
                        cameraConfigPillSelected={cameraConfigPillSelected}
                        vehicleClassOptions={vehicleClassOptions}
                        setFormDataCamera={setFormDataCamera}
                        setCameraChanges={setCameraChanges}
                        setVehicleClass={setVehicleClass}
                        formDataCamera={formDataCamera}
                        confirmChanges={confirmChanges}
                        cameraChanges={cameraChanges}
                        vehicleClass={vehicleClass}
                        setErrors={setErrors}
                        errors={errors}
                        units={units}
                        bulk={bulk}
                        searchTags={searchTags}
                    />
                }
                {
                    !bulk &&
                    <Main
                        setCameraConfigPillSelected={setCameraConfigPillSelected}
                        cameraConfigPillSelected={cameraConfigPillSelected}
                        vehicleClassOptions={vehicleClassOptions}
                        setFormDataCamera={setFormDataCamera}
                        setCameraChanges={setCameraChanges}
                        setVehicleClass={setVehicleClass}
                        formDataCamera={formDataCamera}
                        confirmChanges={confirmChanges}
                        cameraChanges={cameraChanges}
                        vehicleClass={vehicleClass}
                        setErrors={setErrors}
                        errors={errors}
                        units={units}
                        bulk={bulk}
                    />
                }
            </ModalBody>
            <ModalFooter className="camera-setup-footer">
                <Button
                    disabled={false}
                    onClick={(e) => {
                        e.preventDefault();
                        if (!bulk) {
                            if (confirmChanges) setConfirmChanges(false);
                            else handleClose();
                        } else {
                            if (currentStep === 0) {
                                handleClose()
                            } else {
                                if (currentStep === 1) {
                                    setSelectedCameras([])
                                    dispatch(cameraSetupUpdateUnits([]))
                                } else if (currentStep === 2) {
                                    setCameraChanges({})

                                    if (units?.length && camerasDefaultConfigure && fleetDefaultConfigure.settings && bulk) {
                                        const alarmTypes = ['USING_CELLPHONE', 'NO_SEATBELT', 'DISTRACTED', 'NO_DRIVER', 'TAILGATING', 'LANE_DEPARTURE', 'PASSENGERS_NO_SEATBELT'];
                                        let defaultCount = vidFleetConfigLimitDefault.advanced.safetyAssistance.alarm.count.default || 0;

                                        //returns changes made when returning to the camera list
                                        let currentCamerasConfig = mergeObjects(fleetDefaultConfigure.settings, camerasDefaultConfigure);
                                        if (currentCamerasConfig) {
                                            const alarmCounts = calculateAlarmCounts(currentCamerasConfig, alarmTypes, defaultCount);
                                            const updatedDataCamera = updateDataCamera(currentCamerasConfig, alarmCounts);
                                            setFormDataCamera(updatedDataCamera);
                                        }
                                    }
                                }
                                setCurrentStep(prevState => prevState - 1)
                            }
                        }
                    }}
                    variant="secondary"
                    style={{ marginRight: 5 }}
                >
                    {!bulk ? messages['cancel'] : currentStep === 0 ? messages['cancel'] : messages['back']}
                </Button>
                <Button
                    isDisabled={
                        (!bulk && _.isEmpty(cameraChanges)) ||
                        errors?.length ||
                        formDataCamera?.noData ||
                        (bulk && currentStep === 0 && !deviceModelSelected) ||
                        (bulk && currentStep === 1 && !selectedCameras.length) ||
                        (bulk && currentStep === 2 && _.isEmpty(cameraChanges))
                    }
                    onClick={(e) => {
                        e.preventDefault()
                        if (!bulk) {
                            if (!confirmChanges) setConfirmChanges(true);
                            else applyChanges()
                        } else {
                            if (currentStep === 3) applyChanges()
                            else setCurrentStep(prevState => prevState + 1)
                        }
                    }}
                    variant="primary"
                >
                    {!bulk ? messages['save'] : currentStep === 3 ? messages['save'] : messages['next']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default CameraSetup